import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, Chip, Collapse, LinearProgress, ListItemText, MenuItem, Select, TextField, Typography } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import WorkOutlinedIcon from '@mui/icons-material/WorkOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import { AppButton } from '../../../components/UI';
import { ConfirmDialog, EmptyState, ErrorBanner, Panel, PanelHeader, SectionTitle, Tags } from './ui';
import { fetchCandidates, fetchSourcedJobs, promoteMatch, runMatching } from '../api';
import { CANDIDATE_TENANTS, MISSION_TENANTS, SEARCH_MODES } from '../constants';

const SCORE_LABELS = {
  core_skills_score: 'Compétences',
  role_score: 'Rôle',
  experience_score: 'Expérience',
  language_score: 'Langues',
  seniority_score: 'Séniorité',
  semantic_score: 'Sémantique',
};

const pct = (v) => Math.round(Math.max(0, Math.min(1, Number(v) || 0)) * 100);
const scoreTone = (v) => (v >= 70 ? '#16a34a' : v >= 45 ? '#b45309' : '#dc2626');

/** Select whose options carry an explanation line. */
const ExplainedSelect = ({ label, value, onChange, options }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography component="label" sx={{ display: 'block', mb: 0.6, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#64748b' }}>{label}</Typography>
    <Select
      fullWidth
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      renderValue={(v) => options.find((o) => o.value === v)?.label}
      MenuProps={{ slotProps: { paper: { sx: { maxWidth: 380 } } } }}
    >
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value} sx={{ whiteSpace: 'normal', alignItems: 'flex-start' }}>
          <ListItemText
            primary={o.label}
            secondary={o.description}
            slotProps={{ primary: { sx: { fontSize: '0.86rem', fontWeight: 700 } }, secondary: { sx: { fontSize: '0.74rem' } } }}
          />
        </MenuItem>
      ))}
    </Select>
  </Box>
);

const ResultCard = ({ result, rank, candidateResult, onPromote }) => {
  const [open, setOpen] = useState(false);
  const p = result.properties || {};
  const score = pct(result.score);
  const scores = Object.entries(result.individual_scores || {}).filter(([k]) => SCORE_LABELS[k]);
  const title = candidateResult ? p.full_name || 'Candidat sans nom' : p.title || 'Mission sans titre';
  const meta = candidateResult
    ? [
      { icon: <PersonOutlinedIcon />, text: p.roles_held?.[0] || p.primary_role || 'Rôle non renseigné' },
      { icon: <WorkOutlinedIcon />, text: p.years_of_experience != null ? `${p.years_of_experience} an(s) d'expérience` : 'Expérience non renseignée' },
    ]
    : [
      { icon: <BusinessOutlinedIcon />, text: p.company || 'Entreprise non renseignée' },
      { icon: <WorkOutlinedIcon />, text: p.employment_type || 'Contrat non renseigné' },
    ];

  return (
    <Panel>
      <Box sx={{ p: 2.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: rank === 1 ? '#03407b' : '#ebf4fb', color: rank === 1 ? '#fff' : '#03407b', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {rank}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>{title}</Typography>
            <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 2, color: '#64748b' }}>
              {[...meta, { icon: <PlaceOutlinedIcon />, text: p.location || 'Localisation non renseignée' }].map((m) => (
                <Box key={m.text} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.78rem', '& svg': { fontSize: 15 } }}>{m.icon}{m.text}</Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ textAlign: 'center', px: 1.75, py: 1, borderRadius: '12px', bgcolor: '#f8fbfe', border: '1px solid #e2edf7', flexShrink: 0 }}>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: scoreTone(score), lineHeight: 1 }}>{score}%</Typography>
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', mt: 0.25 }}>Score global</Typography>
          </Box>
        </Box>

        {scores.length > 0 && (
          <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 1 }}>
            {scores.map(([k, v]) => (
              <Box key={k} sx={{ p: 1.1, borderRadius: '10px', bgcolor: '#f8fafc', border: '1px solid #eef2f7' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.6 }}>
                  <Typography sx={{ fontSize: '0.74rem', color: '#64748b', fontWeight: 600 }}>{SCORE_LABELS[k]}</Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: '#0f172a', fontWeight: 800 }}>{pct(v)}%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={pct(v)} sx={{ height: 5, borderRadius: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#03407b', borderRadius: 3 } }} />
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <Box><SectionTitle>Compétences communes</SectionTitle><Tags values={result.common_skills} color="#16a34a" bg="#f0fdf4" empty="Aucune correspondance exacte." /></Box>
          <Box><SectionTitle>Compétences demandées manquantes</SectionTitle><Tags values={result.missing_skills} color="#b45309" bg="#fffbeb" empty="Aucune compétence manquante." /></Box>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', gap: 1.25, p: 1.5, borderRadius: '10px', bgcolor: '#f0f7fd', border: '1px solid #cfe0f1' }}>
          <AutoAwesomeOutlinedIcon sx={{ color: '#03407b', fontSize: 18, mt: 0.2 }} />
          <Box>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#03407b' }}>Pourquoi ce classement ?</Typography>
            <Typography sx={{ fontSize: '0.82rem', color: '#334155', lineHeight: 1.55 }}>{result.ranking_reason || 'Classement calculé par le moteur de matching.'}</Typography>
          </Box>
        </Box>

        <Collapse in={open}>
          <Box sx={{ mt: 2, p: 1.5, borderRadius: '10px', bgcolor: '#f8fafc', border: '1px solid #eef2f7' }}>
            <Typography sx={{ fontSize: '0.82rem', color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
              {String(p.summary || (candidateResult ? p.work_experience : p.job_description) || 'Description non renseignée.').replace(/\*\*/g, '')}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 1 }}>Méthode : {result.search_method || '—'}</Typography>
          </Box>
        </Collapse>

        <Box sx={{ mt: 2, pt: 1.75, borderTop: '1px solid #eef2f7', display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
          <AppButton variant="secondary" endIcon={<ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />} onClick={() => setOpen((o) => !o)}>
            {open ? 'Masquer le détail' : `Voir ${candidateResult ? 'le candidat' : 'la mission'}`}
          </AppButton>
          <AppButton startIcon={<VerifiedOutlinedIcon />} disabled={result.promoted} onClick={() => onPromote(result)}>
            {result.promoted ? (candidateResult ? 'Candidat promu' : 'Mission promue') : `Promouvoir ${candidateResult ? 'le candidat' : 'la mission'}`}
          </AppButton>
        </Box>
      </Box>
    </Panel>
  );
};

const MatchingView = ({ direction, snackbarShowMessage }) => {
  const candidateFirst = direction === 'candidate-missions';
  const directionRef = useRef(direction);
  directionRef.current = direction;
  const [sources, setSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(true);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState('vecteur');
  const [scope, setScope] = useState('official');
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(null);
  const [promoting, setPromoting] = useState(false);

  useEffect(() => {
    let alive = true;
    setSources([]);
    setSelected(null);
    setScope('official');
    setResults(null);
    setError('');
    setLoadingSources(true);
    (candidateFirst ? fetchCandidates('stored', 500) : fetchSourcedJobs(500))
      .then((r) => {
        if (!alive) return;
        setSources((r.items || []).map((item) => {
          const p = item.payload || item;
          return {
            id: candidateFirst ? item.candidate_uuid : item.uuid,
            label: candidateFirst ? p.full_name : p.title,
            detail: candidateFirst ? p.roles_held?.[0] : p.company,
          };
        }).filter((o) => o.id && o.label));
      })
      .catch((e) => alive && setError(e.message))
      .finally(() => alive && setLoadingSources(false));
    return () => { alive = false; };
  }, [candidateFirst]);

  const launch = async () => {
    const requested = direction;
    setBusy(true);
    setError('');
    try {
      const r = await runMatching(candidateFirst ? 'candidates-to-missions' : 'missions-to-candidates', selected.id, mode, 10, scope);
      if (directionRef.current !== requested) return;
      setResults(r.results || []);
      snackbarShowMessage(`${r.total || (r.results || []).length} résultat(s) de matching.`, 'success');
    } catch (e) {
      if (directionRef.current === requested) {
        setError(e.message);
        snackbarShowMessage(e.message, 'error');
      }
    } finally {
      if (directionRef.current === requested) setBusy(false);
    }
  };

  const promote = async () => {
    setPromoting(true);
    try {
      await promoteMatch(candidateFirst ? 'candidates' : 'missions', pending.match_id);
      setResults((v) => v.map((x) => (x.match_id === pending.match_id ? { ...x, promoted: true } : x)));
      snackbarShowMessage(candidateFirst ? 'Candidat promu vers le référentiel officiel.' : 'Mission promue vers le référentiel officiel.', 'success');
      setPending(null);
    } catch (e) {
      snackbarShowMessage(e.message, 'error');
    } finally {
      setPromoting(false);
    }
  };

  const tenants = candidateFirst ? MISSION_TENANTS : CANDIDATE_TENANTS;
  const tenantLabel = tenants.find((t) => t.value === scope)?.label;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Panel sx={{ overflow: 'visible' }}>
        <PanelHeader
          icon={<CompareArrowsIcon />}
          title={candidateFirst ? 'Trouver des missions pour un candidat sourcé' : 'Trouver des candidats pour une mission sourcée'}
          subtitle="Choisissez la source, l'espace à comparer et la méthode : les 10 meilleures correspondances sont classées et expliquées."
        />
        <Box sx={{ p: 2.5, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1.4fr) minmax(0,1fr) minmax(0,0.8fr) auto' }, gap: 1.5, alignItems: 'end' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="label" sx={{ display: 'block', mb: 0.6, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#64748b' }}>
              {candidateFirst ? 'Candidat sourcé' : 'Mission sourcée'}
            </Typography>
            <Autocomplete
              size="small"
              options={sources}
              loading={loadingSources}
              value={selected}
              onChange={(e, v) => { setSelected(v); setResults(null); }}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              noOptionsText="Aucun résultat"
              loadingText="Chargement…"
              renderOption={(props, o) => (
                <li {...props} key={o.id}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: '0.86rem', fontWeight: 700 }}>{o.label}</Typography>
                    {o.detail && <Typography noWrap sx={{ fontSize: '0.74rem', color: '#64748b' }}>{o.detail}</Typography>}
                  </Box>
                </li>
              )}
              renderInput={(params) => <TextField {...params} placeholder={`Rechercher ${candidateFirst ? 'un candidat' : 'une mission'}…`} />}
            />
          </Box>
          <ExplainedSelect label={candidateFirst ? 'Comparer avec' : 'Chercher parmi'} value={scope} onChange={(v) => { setScope(v); setResults(null); }} options={tenants} />
          <ExplainedSelect label="Méthode" value={mode} onChange={setMode} options={SEARCH_MODES} />
          <AppButton size="large" startIcon={<PlayArrowRoundedIcon />} loading={busy} disabled={!selected} onClick={launch} sx={{ height: 40 }}>
            Lancer le matching
          </AppButton>
        </Box>
        {error && <Box sx={{ px: 2.5, pb: 2.5 }}><ErrorBanner>{error}</ErrorBanner></Box>}
      </Panel>

      {results === null ? (
        <Panel>
          <EmptyState
            icon={<CompareArrowsIcon />}
            title="Aucun matching lancé"
            text={`Sélectionnez ${candidateFirst ? 'un candidat' : 'une mission'} puis cliquez sur « Lancer le matching ». Vous pourrez ensuite promouvoir les meilleures correspondances vers le référentiel officiel.`}
          />
        </Panel>
      ) : results.length === 0 ? (
        <Panel><EmptyState icon={<CompareArrowsIcon />} title="Aucune correspondance" text="Essayez une autre méthode ou un autre espace de comparaison." /></Panel>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>Résultats classés</Typography>
            <Chip size="small" label={tenantLabel} sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#ebf4fb', color: '#03407b' }} />
            <Typography sx={{ ml: 'auto', fontSize: '0.78rem', color: '#64748b' }}>{results.length} correspondance(s)</Typography>
          </Box>
          {results.map((r, i) => (
            <ResultCard key={r.match_id} result={r} rank={i + 1} candidateResult={!candidateFirst} onPromote={setPending} />
          ))}
        </>
      )}

      <ConfirmDialog
        open={!!pending}
        title={candidateFirst ? 'Promouvoir ce candidat ?' : 'Promouvoir cette mission ?'}
        text={candidateFirst
          ? `Le candidat sera copié vers le référentiel officiel des candidats, sur la base de sa correspondance avec « ${pending?.properties?.title || 'cette mission'} ».`
          : `La mission sera copiée vers le référentiel officiel des missions, sur la base de sa correspondance avec « ${pending?.properties?.full_name || 'ce candidat'} ».`}
        confirmLabel="Promouvoir"
        busy={promoting}
        onConfirm={promote}
        onClose={() => setPending(null)}
      />
    </Box>
  );
};

export default MatchingView;
